import React from 'react';

// A short timer rather than `requestAnimationFrame`: the frame callbacks are paused in a background tab,
// and then neither the reopen nor the media query read ever happens
const SETTLE_DELAY = 16;

/**
 * Called when the viewport turns from portrait to landscape or back. Both events are listened to: 
 * a phone fires `orientationchange`, a desktop window or DevTools' device mode only `resize`.
 */
export function useOnOrientationChange(onFlip: () => void) {
  React.useEffect(() => {
    let landscape = window.innerWidth > window.innerHeight;
    let timer = 0;

    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const next = window.innerWidth > window.innerHeight;
        if (next === landscape) {
          return;
        }
        landscape = next;
        onFlip();
      }, SETTLE_DELAY);
    };

    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, [onFlip]);
}

/**
 * Whether `query` currently matches, re-read on rotation and on resize. The read is what counts: a phone
 * (and a browser driven by DevTools' device mode) does not always deliver a `change` event for the list
 */
export function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);

  React.useEffect(() => {
    let timer = 0;
    const read = () => setMatches(window.matchMedia(query).matches);
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(read, SETTLE_DELAY);
    };

    read();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [query]);

  return matches;
}

/**
 * Closes and reopens whatever `setOpen` controls, one delay apart
 */
export function useReopenOnOrientation(open: boolean, setOpen: (open: boolean) => void) {
  const reopen = React.useCallback(() => {
    if (!open) {
      return;
    }
    setOpen(false);
    // The timer is what reopens it: a list opened before the turn is closed by antd when its search input goes
    // away (landscape), and that has to have settled before it is opened again
    window.setTimeout(() => setOpen(true), SETTLE_DELAY);
  }, [open, setOpen]);

  useOnOrientationChange(reopen);
}
